import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Add, Search, Clear } from "@mui/icons-material";
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import AppointmentsTable from "../components/AppointmentsTable";
import AppointmentFormModal from "../components/AppointmentFormModal";
import AppointmentDeleteConfirmationDialog from "../components/AppointmentDeleteConfirmationDialog";
import AppointmentInfoDialog from "../components/AppointmentInfoDialog";
import Pagination from "../components/Pagination";
import ClientFormModal from "../components/ClientFormModal";
import { useAppointmentsApi } from "../hooks/useAppointmentsApi";
import { useServiceTemplatesApi } from "../hooks/useServiceTemplatesApi";
import { useHairdressersApi } from "../hooks/useHairdressersApi";
import { useClientsApi } from "../hooks/useClientsApi";
import { Appointment, AppointmentStatus, Client, Hairdresser, ServiceTemplate, PaginationMeta } from "../types/entities";
import { AppointmentFormData, AppointmentServiceFormData, ClientFormData } from "../types/forms";

const AppointmentsPage: React.FC = () => {
  const { t } = useTranslation();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [paginationMeta, setPaginationMeta] = useState<PaginationMeta | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [clients, setClients] = useState<Client[]>([]);
  const [hairdressers, setHairdressers] = useState<Hairdresser[]>([]);
  const [serviceTemplates, setServiceTemplates] = useState<ServiceTemplate[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openInfoDialog, setOpenInfoDialog] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<AppointmentStatus | "">("");
  const [startDateFilter, setStartDateFilter] = useState("");
  const [endDateFilter, setEndDateFilter] = useState("");
  const [formData, setFormData] = useState<AppointmentFormData>({
    client_id: 0,
    hairdresser_id: 0, // Will be set to first hairdresser when hairdressers are loaded
    date: "",
    status: AppointmentStatus.NEW,
    notes: "",
    services: [],
  });

  // Client creation state
  const [openClientModal, setOpenClientModal] = useState(false);
  const [clientFormData, setClientFormData] = useState<ClientFormData>({
    name: "",
    phone_number: "",
  });

  const {
    error: appointmentsError,
    fetchAppointments,
    createAppointment: createAppointmentApi,
    updateAppointment: updateAppointmentApi,
    deleteAppointment: deleteAppointmentApi,
  } = useAppointmentsApi();

  const {
    error: serviceTemplatesError,
    fetchServiceTemplates,
  } = useServiceTemplatesApi();

  const {
    error: hairdressersError,
    fetchHairdressers,
  } = useHairdressersApi();

  const {
    error: clientsError,
    fetchClients,
    createClient: createClientApi,
    updateClient: updateClientApi,
  } = useClientsApi();

  const loadData = async (page: number = currentPage, limit: number = itemsPerPage): Promise<void> => {
    try {
      // Ensure page and limit are numbers
      const pageNum = Number(page);
      const limitNum = Number(limit);
      const queryParams: any = { 
        page: pageNum, 
        limit: limitNum,
        sortBy: 'updatedAt',
        sortOrder: 'DESC'
      };
      
      // Add filter parameters
      if (searchTerm && searchTerm.trim()) queryParams.search = searchTerm.trim();
      if (statusFilter) queryParams.status = statusFilter;
      if (startDateFilter) queryParams.dateFrom = startDateFilter;
      if (endDateFilter) queryParams.dateTo = endDateFilter;

      const [appointmentsResponse, serviceTemplatesResponse, hairdressersData, clientsResponse] = await Promise.all([
        fetchAppointments(queryParams),
        fetchServiceTemplates({ limit: 100 }), // Load more for dropdown
        fetchHairdressers(),
        fetchClients({ limit: 10000 }), // Load all clients for dropdown
      ]);
      
      setAppointments(appointmentsResponse.data);
      setPaginationMeta(appointmentsResponse.meta);
      setServiceTemplates(serviceTemplatesResponse.data);
      setHairdressers(hairdressersData);
      setClients(clientsResponse.data);
    } catch (err) {
      // Errors are handled by the hooks
    }
  };

  const createAppointment = async (data?: AppointmentFormData | Partial<AppointmentFormData>): Promise<void> => {
    try {
      // Use provided data or fallback to form data
      const dataToCreate = data || formData;
      await createAppointmentApi(dataToCreate as AppointmentFormData);
      await loadData();
      setOpenCreateModal(false);
      resetForm();
      toast.success(t('appointments.appointmentCreated'));
    } catch (err) {
      // Error is handled by the hook
    }
  };

  const updateAppointment = async (changedData?: Partial<AppointmentFormData> | any): Promise<void> => {
    if (!selectedAppointment) return;

    try {
      // If this is a finalized appointment (has payments), just refresh the data
      if (changedData && changedData.payments && changedData.payments.length > 0) {
        await loadData();
        setOpenEditModal(false);
        setSelectedAppointment(null);
        resetForm();
        return;
      }

      // Use changed data if provided, otherwise use all form data
      const dataToUpdate = changedData || formData;
      await updateAppointmentApi(selectedAppointment.id, dataToUpdate);
      await loadData();
      setOpenEditModal(false);
      setSelectedAppointment(null);
      resetForm();
      toast.success(t('appointments.appointmentUpdated'));
    } catch (err) {
      // Error is handled by the hook
    }
  };

  const deleteAppointment = async (): Promise<void> => {
    if (!selectedAppointment) return;

    try {
      await deleteAppointmentApi(selectedAppointment.id);
      await loadData();
      setOpenDeleteDialog(false);
      setSelectedAppointment(null);
      toast.success(t('appointments.appointmentDeleted'));
    } catch (err) {
      // Error is handled by the hook
    }
  };

  const resetForm = (): void => {
    setFormData({
      client_id: 0,
      hairdresser_id: hairdressers.length > 0 ? hairdressers[0].id : 0,
      date: "",
      status: AppointmentStatus.NEW,
      notes: "",
      services: [],
    });
  };

  // Helper function to set minutes to 0
  const setMinutesToZero = (date: Date): string => {
    const dateWithZeroMinutes = new Date(date);
    dateWithZeroMinutes.setMinutes(0, 0, 0); // Set minutes, seconds, and milliseconds to 0
    return dateWithZeroMinutes.toISOString();
  };

  const handleInputChange =
    (field: keyof AppointmentFormData) =>
    (event: any): void => {
      const value = event.target.value;
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

  const handleServiceChange = (index: number, field: keyof AppointmentServiceFormData, value: any): void => {
    setFormData((prev) => ({
      ...prev,
      services: prev.services.map((service, i) =>
        i === index ? { ...service, [field]: value } : service
      ),
    }));
  };

  const handleAddService = (serviceTemplate: ServiceTemplate): void => {
    const newService: AppointmentServiceFormData = {
      name: serviceTemplate.name,
      description: serviceTemplate.description || "",
      price: Math.round(parseFloat(serviceTemplate.price)),
      duration: serviceTemplate.estimated_duration,
    };
    setFormData((prev) => ({
      ...prev,
      services: [...prev.services, newService],
    }));
  };

  const handleRemoveService = (index: number): void => {
    setFormData((prev) => ({
      ...prev,
      services: prev.services.filter((_, i) => i !== index),
    }));
  };

  const handleEditClick = (appointment: Appointment): void => {
    setSelectedAppointment(appointment);
    setFormData({
      client_id: appointment.client?.id || 0,
      hairdresser_id: appointment.hairdresser?.id || 0,
      date: appointment.date.toString(),
      status: appointment.status,
      notes: appointment.notes || "",
      services: appointment.services?.map(service => ({
        name: service.name,
        description: service.description || "",
        price: Math.round(parseFloat(service.price)),
        duration: service.duration,
      })) || [],
    });
    setOpenEditModal(true);
  };

  const handleDeleteClick = (appointment: Appointment): void => {
    setSelectedAppointment(appointment);
    setOpenDeleteDialog(true);
  };

  const handleInfoClick = (appointment: Appointment): void => {
    setSelectedAppointment(appointment);
    setOpenInfoDialog(true);
  };

  const handleClearSearch = (): void => {
    setSearchTerm("");
    setStatusFilter("");
    setStartDateFilter("");
    setEndDateFilter("");
    setCurrentPage(1); // Reset to first page when clearing filters
  };

  const handlePageChange = (page: number): void => {
    setCurrentPage(page);
    loadData(page, itemsPerPage);
  };

  const handleLimitChange = (limit: number): void => {
    setItemsPerPage(limit);
    setCurrentPage(1);
    loadData(1, limit);
  };

  const handleModalClose = (): void => {
    setOpenCreateModal(false);
    setOpenEditModal(false);
    resetForm();
    // Refresh appointments to get updated data (including payments for finalized appointments)
    const queryParams: any = { 
      page: currentPage, 
      limit: itemsPerPage,
      sortBy: 'updatedAt',
      sortOrder: 'DESC'
    };
    
    // Add filter parameters
    if (searchTerm && searchTerm.trim()) queryParams.search = searchTerm.trim();
    if (statusFilter) queryParams.status = statusFilter;
    if (startDateFilter) queryParams.dateFrom = startDateFilter;
    if (endDateFilter) queryParams.dateTo = endDateFilter;
    
    fetchAppointments(queryParams);
  };

  const handleClientModalClose = (): void => {
    setOpenClientModal(false);
    setClientFormData({ name: "", phone_number: "" });
  };

  // Client creation handlers
  const handleClientInputChange = (field: keyof ClientFormData) => (event: React.ChangeEvent<HTMLInputElement>): void => {
    setClientFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const createClient = async (data?: ClientFormData | Partial<ClientFormData>): Promise<void> => {
    try {
      // Use provided data or fallback to client form data
      const dataToCreate = data || clientFormData;
      const newClient = await createClientApi(dataToCreate as ClientFormData);
      // Refresh clients list
      const clientsResponse = await fetchClients({ limit: 10000 });
      setClients(clientsResponse.data);
      
      // Set the newly created client as selected
      setFormData(prev => ({
        ...prev,
        client_id: newClient.id
      }));
      
      setOpenClientModal(false);
      setClientFormData({ name: "", phone_number: "" });
      toast.success(t('clients.clientCreated'));
    } catch (err) {
      // Error is handled by the hook
    }
  };

  const handleCreateClient = (searchText?: string): void => {
    // Pre-fill the name field with the search text if provided
    if (searchText && searchText.trim()) {
      setClientFormData({ name: searchText.trim(), phone_number: "" });
    } else {
      setClientFormData({ name: "", phone_number: "" });
    }
    setOpenClientModal(true);
  };

  // Wrapper function to update client and refresh clients data
  const handleUpdateClient = async (id: number, data: Partial<ClientFormData>): Promise<Client> => {
    const updatedClient = await updateClientApi(id, data);
    // Refresh clients data after update
    const clientsResponse = await fetchClients({ limit: 10000 });
    setClients(clientsResponse.data);
    return updatedClient;
  };

  // No frontend filtering needed - backend handles all filtering

  useEffect(() => {
    const initializeData = async () => {
      try {
        await loadData();
      } finally {
        setInitialLoading(false);
      }
    };

    initializeData();
  }, []);

  // Reload data when filters change (with debounce for search)
  useEffect(() => {
    if (!initialLoading) {
      setCurrentPage(1); // Reset to first page when filters change
      
      // Debounce search term to avoid too many requests
      const timeoutId = setTimeout(() => {
        loadData(1, itemsPerPage);
      }, searchTerm ? 300 : 0); // 300ms delay for search, immediate for other filters
      
      return () => clearTimeout(timeoutId);
    }
  }, [searchTerm, statusFilter, startDateFilter, endDateFilter, itemsPerPage, initialLoading]);

  // Set default hairdresser when hairdressers are loaded
  useEffect(() => {
    if (hairdressers.length > 0 && formData.hairdresser_id === 0) {
      setFormData(prev => ({
        ...prev,
        hairdresser_id: hairdressers[0].id
      }));
    }
  }, [hairdressers, formData.hairdresser_id]);

  if (initialLoading) {
    return (
      <Container>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
  <Container>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ 
          mb: 3,
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 2 : 0,
          textAlign: isMobile ? 'center' : 'left'
        }}
      >
        <Typography variant={isMobile ? "h5" : "h4"} gutterBottom sx={{ mb: 0 }}>
          {t('appointments.title')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            // Set current date/time with minutes set to 0
            const now = new Date();
            const dateStr = setMinutesToZero(now);
            
            setFormData({
              client_id: 0,
              hairdresser_id: hairdressers.length > 0 ? hairdressers[0].id : 0,
              date: dateStr,
              status: AppointmentStatus.NEW,
              notes: "",
              services: [],
            });
            
            setOpenCreateModal(true);
          }}
          size={isMobile ? 'medium' : 'large'}
          fullWidth={isMobile}
        >
          {t('appointments.addAppointment')}
        </Button>
      </Box>

      {(appointmentsError || serviceTemplatesError || hairdressersError || clientsError) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {appointmentsError?.message || serviceTemplatesError?.message || hairdressersError?.message || clientsError?.message}
        </Alert>
      )}

      {/* Always show filter controls */}
      <Box sx={{ mb: 3 }}>
        <Box display="flex" gap={2} sx={{ mb: 2 }}>
          <TextField
            fullWidth
            placeholder={t('appointments.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="clear search"
                    onClick={handleClearSearch}
                    edge="end"
                    size="small"
                  >
                    <Clear />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>{t('common.status')}</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as AppointmentStatus | "")}
              label={t('common.status')}
            >
              <MenuItem value="">{t('common.all')}</MenuItem>
              <MenuItem value={AppointmentStatus.NEW}>{t('appointments.new')}</MenuItem>
              <MenuItem value={AppointmentStatus.CONFIRMED}>{t('appointments.confirmed')}</MenuItem>
              <MenuItem value={AppointmentStatus.COMPLETED}>{t('appointments.completed')}</MenuItem>
              <MenuItem value={AppointmentStatus.CANCELED}>{t('appointments.canceled')}</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box display="flex" gap={2} sx={{ mb: 2 }}>
          <TextField
            label={t('appointments.fromDate')}
            type="date"
            value={startDateFilter}
            onChange={(e) => setStartDateFilter(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
            InputProps={{
              endAdornment: startDateFilter && (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="clear start date"
                    onClick={() => setStartDateFilter("")}
                    edge="end"
                    size="small"
                  >
                    <Clear />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 150 }}
          />
          <TextField
            label={t('appointments.toDate')}
            type="date"
            value={endDateFilter}
            onChange={(e) => setEndDateFilter(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              min: startDateFilter || undefined
            }}
            InputProps={{
              endAdornment: endDateFilter && (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="clear end date"
                    onClick={() => setEndDateFilter("")}
                    edge="end"
                    size="small"
                  >
                    <Clear />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 150 }}
            error={!!(startDateFilter && endDateFilter && endDateFilter < startDateFilter)}
            helperText={startDateFilter && endDateFilter && endDateFilter < startDateFilter ? t('appointments.toDateError') : ""}
          />
        </Box>
      </Box>

      {/* Show results or no results message */}
      {appointments.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
            {searchTerm || statusFilter || startDateFilter || endDateFilter 
              ? t('appointments.noAppointmentsMatching') 
              : t('appointments.noAppointmentsFound')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {searchTerm || statusFilter || startDateFilter || endDateFilter 
              ? t('appointments.noAppointmentsMatchingMessage') 
              : t('appointments.noAppointmentsMessage')}
          </Typography>
          {(searchTerm || statusFilter || startDateFilter || endDateFilter) && (
            <Button
              variant="outlined"
              onClick={handleClearSearch}
              startIcon={<Clear />}
            >
              {t('appointments.clearAllFilters')}
            </Button>
          )}
        </Box>
      ) : (
        <>
          <Box sx={{ 
            minHeight: `${Math.max(appointments.length * 60 + 80, 120)}px`, 
            maxHeight: '500px', 
            overflow: 'auto', 
            mb: 2 
          }}>
            <AppointmentsTable
              appointments={appointments}
              onEdit={handleEditClick}
              onDelete={handleDeleteClick}
              onInfo={handleInfoClick}
            />
          </Box>
          {paginationMeta && (
            <Box display="flex" justifyContent="center">
              <Pagination
                meta={paginationMeta}
                onPageChange={handlePageChange}
                onLimitChange={handleLimitChange}
              />
            </Box>
          )}
        </>
      )}

      <AppointmentFormModal
        open={openCreateModal}
        onClose={handleModalClose}
        onSubmit={createAppointment}
        formData={formData}
        onInputChange={handleInputChange}
        onServiceChange={handleServiceChange}
        onAddService={handleAddService}
        onRemoveService={handleRemoveService}
        onCreateClient={handleCreateClient}
        onUpdateClient={handleUpdateClient}
        onRefreshAppointments={() => loadData(currentPage, itemsPerPage)}
        title={t('appointments.addNewAppointment')}
        submitButtonText={t('common.create')}
        clients={clients}
        hairdressers={hairdressers}
        serviceTemplates={serviceTemplates}
        appointments={appointments}
      />

      <AppointmentFormModal
        open={openEditModal}
        onClose={handleModalClose}
        onSubmit={updateAppointment}
        formData={formData}
        onInputChange={handleInputChange}
        onServiceChange={handleServiceChange}
        onAddService={handleAddService}
        onRemoveService={handleRemoveService}
        onCreateClient={handleCreateClient}
        onUpdateClient={handleUpdateClient}
        onRefreshAppointments={() => loadData(currentPage, itemsPerPage)}
        title={t('appointments.editAppointment')}
        submitButtonText={t('common.save')}
        clients={clients}
        hairdressers={hairdressers}
        serviceTemplates={serviceTemplates}
        appointments={appointments}
        isEdit={true}
        editingAppointment={selectedAppointment}
      />

      <AppointmentDeleteConfirmationDialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onConfirm={deleteAppointment}
        appointment={selectedAppointment}
      />

      <AppointmentInfoDialog
        open={openInfoDialog}
        onClose={() => setOpenInfoDialog(false)}
        appointment={selectedAppointment}
        clients={clients}
      />

      <ClientFormModal
        open={openClientModal}
        onClose={handleClientModalClose}
        onSubmit={createClient}
        formData={clientFormData}
        onInputChange={handleClientInputChange}
        title={t('clients.addNewClient')}
        submitButtonText={t('common.create')}
      />
  </Container>
);
};

export default AppointmentsPage;
